import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, X, Wand2, Loader2, Check, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NOTES_API = `${BACKEND_URL}/api/notes`;
const BUILD_API = `${BACKEND_URL}/api/wingman/build_application`;

/**
 * Modal that pulls a lead's saved notes, lets the LO add more inline,
 * sends them to /api/wingman/build_application, then dispatches
 * `wingman:fillApp` events for each extracted field. Caller passes
 * onLeadPatch to update lead-level fields too.
 */
export default function BuildFromNotesModal({ open, leadId, leadName, onClose, onLeadPatch }) {
  const [notes, setNotes] = useState([]);
  const [extra, setExtra] = useState('');
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open || !leadId) return;
    setLoading(true);
    axios.get(`${NOTES_API}?leadId=${encodeURIComponent(leadId)}`)
      .then((r) => setNotes(r.data || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [open, leadId]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const totalChars = notes.reduce((s, n) => s + (n.body?.length || 0), 0) + extra.length;
  const hasMaterial = totalChars > 20;

  const build = async () => {
    if (!hasMaterial) {
      toast.error('Need more notes — add at least a sentence about the borrower.');
      return;
    }
    setBuilding(true);
    try {
      const r = await axios.post(BUILD_API, {
        lead_id: leadId,
        notes_text: extra.trim() || null,
      });
      const fields = r.data.fields || {};
      const coBorrower = r.data.co_borrower || {};
      const leadPatch = r.data.lead_patch || {};
      const summary = r.data.summary || 'Application built from notes.';

      // Apply application-form fields one-by-one via existing wingman:fillApp event
      let applied = 0;
      for (const [field, value] of Object.entries(fields)) {
        window.dispatchEvent(new CustomEvent('wingman:fillApp', {
          detail: { field, value },
        }));
        applied += 1;
      }

      // Apply lead-level patch (name/email/phone/co_borrower/etc.) via callback
      if (onLeadPatch && Object.keys(leadPatch).length) {
        await onLeadPatch(leadPatch);
      }

      const coFieldCount = Object.keys(coBorrower).length;
      const descBits = [summary];
      if (coFieldCount) {
        const coName = [coBorrower.firstName, coBorrower.lastName].filter(Boolean).join(' ') || 'Co-borrower';
        descBits.push(`↳ Co-borrower: ${coName} (${coFieldCount} field${coFieldCount === 1 ? '' : 's'}).`);
      }

      toast.success(
        `Built ${applied} primary field${applied === 1 ? '' : 's'}${coFieldCount ? ` + ${coFieldCount} co-borrower field${coFieldCount === 1 ? '' : 's'}` : ''}`,
        { description: descBits.join(' '), duration: 9000 },
      );
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Build failed — check the notes content.');
    } finally {
      setBuilding(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div
        ref={dialogRef}
        data-testid="build-from-notes-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl rounded-2xl bg-white border hairline shadow-2xl rise-in flex flex-col max-h-[88vh]"
      >
        {/* Header */}
        <div className="relative px-6 py-5 bg-neutral-900 text-white rounded-t-2xl overflow-hidden shrink-0">
          <div className="absolute inset-0 bg-dot-dark opacity-40" />
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[hsl(var(--kf-gold))]/25 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-[hsl(var(--kf-gold))] text-neutral-900 flex items-center justify-center shrink-0">
                <Wand2 className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="display text-[18px] font-bold tracking-tight leading-tight">Build application from notes</div>
                <div className="text-[11.5px] text-white/60 mt-0.5">
                  Wingman reads {leadName ? <>your notes on <span className="font-semibold text-white">{leadName}</span></> : 'your notes'} and auto-fills the 1003.
                </div>
              </div>
            </div>
            <button
              data-testid="build-from-notes-close"
              onClick={onClose}
              className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center text-white/70"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          {/* Saved notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Saved notes for this lead</div>
              <span className="mono text-[10.5px] text-neutral-500">{notes.length} note{notes.length === 1 ? '' : 's'} · {totalChars} chars</span>
            </div>
            {loading ? (
              <div className="rounded-lg border hairline bg-neutral-50/50 p-6 flex items-center justify-center text-[12px] text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading notes…
              </div>
            ) : notes.length === 0 ? (
              <div className="rounded-lg border-dashed border-2 border-neutral-200 bg-neutral-50/40 p-5 text-center">
                <FileText className="h-5 w-5 text-neutral-300 mx-auto mb-1.5" />
                <div className="text-[12.5px] text-neutral-500">No saved notes yet.</div>
                <div className="text-[11px] text-neutral-400 mt-0.5">Add notes below or jot some on the Notes tab first.</div>
              </div>
            ) : (
              <div className="rounded-lg border hairline bg-neutral-50/40 max-h-44 overflow-y-auto divide-y divide-neutral-100">
                {notes.map((n) => (
                  <div key={n.id} className="px-3.5 py-2.5">
                    <div className="text-[12.5px] text-neutral-800 leading-relaxed whitespace-pre-wrap">{n.body}</div>
                    <div className="mt-1 text-[10px] text-neutral-400 mono">{n.author} · {fmtTime(n.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Inline more notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Add more inline (optional)</div>
              <span className="mono text-[10.5px] text-neutral-400">{extra.length} chars</span>
            </div>
            <textarea
              data-testid="build-from-notes-extra"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={5}
              placeholder="e.g. Talked to Megan today — Senior Engineer at Tesla 5y, base $9500/mo. Her husband David works at Dell as a Staff Engineer, 7y, $8500/mo + $500 bonus, credit 728. Looking at $720k home in Austin, 25% down. Credit 742…"
              className="w-full rounded-lg bg-white border hairline text-[13px] px-3 py-2.5 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 leading-relaxed"
            />
          </div>

          {/* What it'll extract */}
          <div className="rounded-lg border hairline bg-[hsl(var(--kf-gold-soft))]/40 p-3.5">
            <div className="flex items-start gap-2.5">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))] mt-0.5 shrink-0" />
              <div className="text-[11.5px] text-neutral-700 leading-relaxed">
                <span className="font-bold text-neutral-900">Wingman will extract</span> personal info, employment + income, credit, property details, and loan terms.
                If your notes mention a <span className="font-semibold">spouse, partner, or co-signer</span>, Wingman will also build the <span className="font-semibold">co-borrower profile</span> automatically.
                Anything not in your notes is left alone — your existing data stays intact.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t hairline bg-neutral-50/40 rounded-b-2xl flex items-center justify-between shrink-0">
          <button
            onClick={onClose}
            disabled={building}
            className="h-9 px-4 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-700 hover:border-neutral-400 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="build-from-notes-go"
            onClick={build}
            disabled={!hasMaterial || building}
            className="h-9 px-5 rounded-md btn-gold text-[12.5px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {building ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reading notes…
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" strokeWidth={2.5} />
                Generate application
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}
